import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  doc, addDoc, updateDoc, collection, serverTimestamp,
  query, where, onSnapshot,
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { CURRICULUM_TEMPLATES, BOARD_LABELS, GRADES } from '../data/curriculumData';
import TaskCreator from './TaskCreator';
import TaskDetail from './TaskDetail';

// ── Utilities ─────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 10);

function makeSubject(name = '', chapters = []) {
  return {
    id: uid(),
    name,
    chapters: chapters.map((title, i) => ({ id: uid(), title, order: i })),
  };
}

function subjectsFromTemplate(board, grade) {
  const raw = CURRICULUM_TEMPLATES[board]?.[grade] ?? [];
  return raw.map((s) => makeSubject(s.name, s.chapters));
}

// ── CurriculumEditor ──────────────────────────────────────────────────────────

/**
 * Props:
 *   existing          – teacherCurricula doc (null when creating)
 *   approvedStudents  – [{studentId, studentName, studentEmail}] for task assignment
 *   onSave()
 *   onCancel()
 */
export default function CurriculumEditor({ existing, approvedStudents = [], onSave, onCancel }) {
  const { currentUser, userProfile } = useAuth();

  // ── Wizard state: 'setup' | 'edit'
  const [step, setStep] = useState(existing ? 'edit' : 'setup');

  // ── Setup step fields
  const [setupMode, setSetupMode] = useState('template'); // 'template' | 'scratch'
  const [selectedBoard, setSelectedBoard] = useState(userProfile?.board || 'CBSE');
  const [selectedGrade, setSelectedGrade] = useState(existing?.grade || 6);

  // ── Editor fields
  const [currName, setCurrName] = useState(existing?.name || '');
  const [subjects, setSubjects] = useState(existing?.subjects || []);
  const [sourceBoard, setSourceBoard] = useState(existing?.sourceBoard || null);

  // ── Expanded subjects in accordion
  const [expanded, setExpanded] = useState({});

  // ── Active chapter edit: { subjectId, chapterId } | null
  const [editingChapter, setEditingChapter] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null); // subjectId | null

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Task panel: null | { mode:'create', subjectName, chapterName }
  //                     | { mode:'detail', task }
  const [taskPanel, setTaskPanel] = useState(null);

  // ── Tasks for this curriculum (only when editing an existing one)
  const [tasks, setTasks] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    if (!existing?.id || !currentUser?.uid) return;
    const unsubTasks = onSnapshot(
      query(collection(db, 'tasks'), where('curriculumId', '==', existing.id)),
      (snap) => setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubSubs = onSnapshot(
      query(collection(db, 'taskSubmissions'), where('teacherId', '==', currentUser.uid)),
      (snap) => setSubmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => { unsubTasks(); unsubSubs(); };
  }, [existing?.id, currentUser?.uid]);

  // Build task lookup: chapterTitle → [task, ...]
  const tasksByChapter = {};
  tasks.forEach((t) => {
    if (!tasksByChapter[t.chapterName]) tasksByChapter[t.chapterName] = [];
    tasksByChapter[t.chapterName].push(t);
  });

  // Submission count per task
  const submissionCountByTask = {};
  submissions.forEach((s) => {
    submissionCountByTask[s.taskId] = (submissionCountByTask[s.taskId] || 0) + 1;
  });

  // Default name when moving to editor
  const computedName = () =>
    setupMode === 'template'
      ? `Class ${selectedGrade} – ${BOARD_LABELS[selectedBoard] || selectedBoard}`
      : `Class ${selectedGrade} – My Curriculum`;

  // ── Step 1: proceed to editor ──────────────────────────────────────────────
  const handleProceed = () => {
    let loaded = [];
    if (setupMode === 'template') {
      loaded = subjectsFromTemplate(selectedBoard, Number(selectedGrade));
      setSourceBoard(selectedBoard);
    } else {
      setSourceBoard(null);
    }
    setSubjects(loaded);
    setCurrName(computedName());
    setStep('edit');
    // Expand first subject by default
    if (loaded.length > 0) {
      setExpanded({ [loaded[0].id]: true });
    }
  };

  // ── Subject operations ────────────────────────────────────────────────────
  const addSubject = () => {
    const s = makeSubject('New Subject', []);
    setSubjects((prev) => [...prev, s]);
    setExpanded((e) => ({ ...e, [s.id]: true }));
    setEditingSubject(s.id);
  };

  const updateSubjectName = (id, name) =>
    setSubjects((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));

  const deleteSubject = (id) => {
    setSubjects((prev) => prev.filter((s) => s.id !== id));
    setExpanded((e) => { const n = { ...e }; delete n[id]; return n; });
  };

  const toggleExpand = (id) =>
    setExpanded((e) => ({ ...e, [id]: !e[id] }));

  // ── Chapter operations ────────────────────────────────────────────────────
  const addChapter = (subjectId) => {
    const ch = { id: uid(), title: 'New Chapter', order: 999 };
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? { ...s, chapters: [...s.chapters, ch] }
          : s
      )
    );
    setEditingChapter({ subjectId, chapterId: ch.id });
  };

  const updateChapterTitle = (subjectId, chapterId, title) =>
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              chapters: s.chapters.map((c) =>
                c.id === chapterId ? { ...c, title } : c
              ),
            }
          : s
      )
    );

  const deleteChapter = (subjectId, chapterId) =>
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? { ...s, chapters: s.chapters.filter((c) => c.id !== chapterId) }
          : s
      )
    );

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!currName.trim()) { setError('Please give this curriculum a name.'); return; }
    if (subjects.length === 0) { setError('Add at least one subject.'); return; }
    if (!currentUser?.uid) { 
      setError('Your login session appears to have expired. Please sign in again.'); 
      return; 
    }
    setError('');
    setSaving(true);
    try {
      const payload = {
        teacherId: currentUser.uid,
        teacherName: userProfile?.name || '',
        name: currName.trim(),
        grade: Number(selectedGrade),
        sourceBoard: sourceBoard || null,
        subjects,
        updatedAt: serverTimestamp(),
      };

      if (existing) {
        await updateDoc(doc(db, 'teacherCurricula', existing.id), payload);
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, 'teacherCurricula'), payload);
      }
      onSave?.();
    } catch (err) {
      console.error('Save curriculum error:', err);
      // Show the real Firestore error code so it's easier to diagnose
      const code = err?.code ? ` (${err.code})` : '';
      setError(`Failed to save${code}. Check browser console for details.`);
    }
    setSaving(false);
  };

  // ── Render: SETUP STEP ─────────────────────────────────────────────────────
  if (step === 'setup') {
    return (
      <div className="dashboard-wrapper">
        <header className="dashboard-header">
          <div className="header-left">
            <span className="header-logo">🪺 LittleNest</span>
            <div className="header-info">
              <h2 className="header-title">Create New Curriculum</h2>
              <p className="header-subtitle">Step 1: Choose your starting point</p>
            </div>
          </div>
          <button className="btn-outline btn-outline--sm" onClick={onCancel}>← Back</button>
        </header>

        <div className="section animate-in" style={{ maxWidth: '800px', margin: '40px auto' }}>
          <div className="setup-card" style={{ 
            background: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(12px)',
            borderRadius: '24px',
            padding: '32px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.05)'
          }}>
            <p className="setup-label" style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', textAlign: 'center' }}>How would you like to build your curriculum?</p>
            
            <div className="role-cards" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
              <button
                className={`role-card ${setupMode === 'template' ? 'role-card--active' : ''}`}
                onClick={() => setSetupMode('template')}
                style={{ 
                  padding: '24px', 
                  borderRadius: '16px', 
                  border: setupMode === 'template' ? '2px solid var(--primary)' : '1px solid rgba(0,0,0,0.1)',
                  background: setupMode === 'template' ? 'var(--primary-light)' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
                <strong style={{ display: 'block', fontSize: '16px', marginBottom: '4px' }}>Board Template</strong>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>Use standard CBSE, ICSE, or Samacheer chapters.</span>
              </button>

              <button
                className={`role-card ${setupMode === 'scratch' ? 'role-card--active' : ''}`}
                onClick={() => setSetupMode('scratch')}
                style={{ 
                  padding: '24px', 
                  borderRadius: '16px', 
                  border: setupMode === 'scratch' ? '2px solid var(--primary)' : '1px solid rgba(0,0,0,0.1)',
                  background: setupMode === 'scratch' ? 'var(--primary-light)' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>✏️</div>
                <strong style={{ display: 'block', fontSize: '16px', marginBottom: '4px' }}>From Scratch</strong>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>Build your own subjects and chapters manually.</span>
              </button>
            </div>

            <div className="field-row" style={{ display: 'grid', gridTemplateColumns: setupMode === 'template' ? '1fr 1fr' : '1fr', gap: '20px', marginBottom: '32px' }}>
              {setupMode === 'template' && (
                <div className="field">
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Board</label>
                  <select 
                    value={selectedBoard} 
                    onChange={(e) => setSelectedBoard(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.1)', background: '#fff' }}
                  >
                    {Object.entries(BOARD_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="field">
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Class / Grade</label>
                <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(Number(e.target.value))}
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.1)', background: '#fff' }}
                >
                  {GRADES.map((g) => (
                    <option key={g} value={g}>Class {g}</option>
                  ))}
                </select>
              </div>
            </div>

            <button className="btn-primary" style={{ width: '100%', padding: '14px', borderRadius: '12px', fontSize: '16px' }} onClick={handleProceed}>
              Continue to Editor →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: EDIT STEP ──────────────────────────────────────────────────────
  if (taskPanel?.mode === 'create') {
    return (
      <div className="dashboard-wrapper">
        <header className="dashboard-header">
          <div className="header-left">
            <span className="header-logo">🪺 LittleNest</span>
            <div className="header-info">
              <h2 className="header-title">{existing?.name || 'New Curriculum'}</h2>
              <p className="header-subtitle">Assigning Task</p>
            </div>
          </div>
          <button className="btn-outline btn-outline--sm" onClick={() => setTaskPanel(null)}>← Back to Editor</button>
        </header>
        <TaskCreator
          curriculumId={existing.id}
          subjectName={taskPanel.subjectName}
          chapterName={taskPanel.chapterName}
          approvedStudents={approvedStudents}
          existingTask={taskPanel.existingTask || null}
          onSave={() => setTaskPanel(null)}
          onCancel={() => setTaskPanel(null)}
        />
      </div>
    );
  }

  if (taskPanel?.mode === 'detail') {
    return (
      <div className="dashboard-wrapper">
        <header className="dashboard-header">
          <div className="header-left">
            <span className="header-logo">🪺 LittleNest</span>
            <div className="header-info">
              <h2 className="header-title">{existing?.name || 'New Curriculum'}</h2>
              <p className="header-subtitle">Task Detail</p>
            </div>
          </div>
          <button className="btn-outline btn-outline--sm" onClick={() => setTaskPanel(null)}>← Back to Editor</button>
        </header>
        <TaskDetail
          task={taskPanel.task}
          approvedStudents={approvedStudents}
          onBack={() => setTaskPanel(null)}
          onEdit={(t) => setTaskPanel({ mode: 'create', subjectName: t.subjectName, chapterName: t.chapterName, existingTask: t })}
        />
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <header className="dashboard-header">
        <div className="header-left">
          <span className="header-logo">🪺 LittleNest</span>
          <div className="header-info">
            <h2 className="header-title">{existing ? 'Edit Curriculum' : 'Build Curriculum'}</h2>
            <p className="header-subtitle">Define subjects and chapters</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button className="btn-outline btn-outline--sm" onClick={() => existing ? onCancel() : setStep('setup')}>
            ← Back
          </button>
          <button className="btn-primary" style={{ width: 'auto', padding: '10px 20px' }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : '💾 Save Curriculum'}
          </button>
        </div>
      </header>

      <div className="section animate-in" style={{ padding: '24px' }}>
        {error && <div className="error-box" style={{ background: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>{error}</div>}

        <div className="field" style={{ marginBottom: 32 }}>
          <label style={{ display: 'block', fontSize: '15px', fontWeight: '700', marginBottom: '10px', color: 'var(--text-primary)' }}>Curriculum Name</label>
          <input
            type="text"
            value={currName}
            onChange={(e) => setCurrName(e.target.value)}
            placeholder="e.g. Class 6 – Science Term 1"
            style={{ 
              width: '100%', 
              padding: '14px 18px', 
              borderRadius: '12px', 
              border: '1px solid rgba(0,0,0,0.1)', 
              fontSize: '16px',
              background: 'rgba(255,255,255,0.7)'
            }}
          />
        </div>

        <div className="subjects-list">
          {subjects.map((subj, si) => (
            <div key={subj.id} className="cv-subject-block" style={{ 
              background: 'rgba(255, 255, 255, 0.4)',
              borderRadius: '16px',
              marginBottom: '16px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              overflow: 'hidden'
            }}>
              <div className="subject-header" style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
                background: expanded[subj.id] ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
                transition: 'all 0.2s ease'
              }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--primary-light)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', marginRight: '16px' }}>
                  {si+1}
                </div>
                {editingSubject === subj.id ? (
                  <input
                    className="subject-name-input"
                    value={subj.name}
                    autoFocus
                    onChange={(e) => updateSubjectName(subj.id, e.target.value)}
                    onBlur={() => setEditingSubject(null)}
                    onKeyDown={(e) => e.key === 'Enter' && setEditingSubject(null)}
                    style={{ flex: 1, padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--primary)' }}
                  />
                ) : (
                  <button className="subject-name" onClick={() => toggleExpand(subj.id)} style={{ flex: 1, border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', padding: 0 }}>
                    <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>{subj.name}</span>
                    <span style={{ marginLeft: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>{subj.chapters.length} chapters</span>
                  </button>
                )}
                <div className="subject-actions" style={{ display: 'flex', gap: '8px' }}>
                  <button className="icon-btn" onClick={() => setEditingSubject(subj.id)} title="Rename">✏️</button>
                  <button className="icon-btn icon-btn--danger" onClick={() => deleteSubject(subj.id)} title="Delete">🗑</button>
                  <button onClick={() => toggleExpand(subj.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', transform: expanded[subj.id] ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▼</button>
                </div>
              </div>

              {expanded[subj.id] && (
                <div className="chapter-list" style={{ padding: '10px 20px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {subj.chapters.map((ch, idx) => {
                    const chTasks = tasksByChapter[ch.title] || [];
                    return (
                      <div key={ch.id} className="chapter-item" style={{ 
                        background: 'rgba(255,255,255,0.4)', 
                        padding: '12px 16px', 
                        borderRadius: '12px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        border: '1px solid rgba(255,255,255,0.2)'
                      }}>
                        <span style={{ fontSize: '13px', fontWeight: '800', opacity: 0.4 }}>{idx + 1}</span>
                        {editingChapter?.subjectId === subj.id && editingChapter?.chapterId === ch.id ? (
                          <input
                            className="chapter-input"
                            value={ch.title}
                            autoFocus
                            onChange={(e) => updateChapterTitle(subj.id, ch.id, e.target.value)}
                            onBlur={() => setEditingChapter(null)}
                            onKeyDown={(e) => e.key === 'Enter' && setEditingChapter(null)}
                            style={{ flex: 1, padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--primary)' }}
                          />
                        ) : (
                          <span className="chapter-title" onClick={() => setEditingChapter({ subjectId: subj.id, chapterId: ch.id })} style={{ flex: 1, cursor: 'pointer', fontSize: '15px', fontWeight: '500' }}>
                            {ch.title}
                          </span>
                        )}

                        {existing && chTasks.length > 0 && (
                          <div className="chapter-task-badges" style={{ display: 'flex', gap: '6px' }}>
                            {chTasks.slice(0, 2).map((t) => (
                              <div key={t.id} onClick={() => setTaskPanel({ mode: 'detail', task: t })} style={{ padding: '4px 10px', borderRadius: '20px', background: 'var(--primary-light)', color: 'var(--primary)', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                                📋 {t.title.length > 12 ? t.title.substring(0, 10) + '…' : t.title}
                              </div>
                            ))}
                            {chTasks.length > 2 && <span style={{ fontSize: '11px', opacity: 0.5 }}>+{chTasks.length - 2}</span>}
                          </div>
                        )}

                        <div className="chapter-item-actions" style={{ display: 'flex', gap: '8px' }}>
                          {existing && (
                            <button className="btn-outline btn-outline--sm" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => setTaskPanel({ mode: 'create', subjectName: subj.name, chapterName: ch.title })}>+ Task</button>
                          )}
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '18px' }} onClick={() => deleteChapter(subj.id, ch.id)} title="Remove">×</button>
                        </div>
                      </div>
                    );
                  })}
                  <button
                    className="add-chapter-btn"
                    onClick={() => addChapter(subj.id)}
                    style={{ padding: '10px', border: '2px dashed rgba(0,0,0,0.1)', borderRadius: '12px', background: 'transparent', textAlign: 'center', cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: '600', marginTop: '8px' }}
                  >
                    + Add New Chapter
                  </button>
                </div>
              )}
            </div>
          ))}
          <button className="btn-primary btn-outline" style={{ width: '100%', padding: '16px', borderRadius: '16px', background: 'rgba(255,255,255,0.5)', border: '2px dashed var(--primary)', color: 'var(--primary)', fontWeight: '700', marginTop: '12px' }} onClick={addSubject}>
            + Add New Subject
          </button>
        </div>
      </div>
    </div>
  );
}
